/**
 * AnnexFormDialog — Create / Edit Annex (phụ lục hợp đồng).
 *
 * Layout 1:1 với Access `frmPurchaseQryAnnexAdd` (Caption "Purchase - Amendment").
 * 2 contract pickers (Contract P / Consignment P) — user chỉ chọn 1.
 * 4 ô context readonly (Quantity contract / Price Unit / Outright Price / PTBF Diffs)
 * auto-populate từ contract đã chọn.
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateAnnexMutation,
  useSuggestAnnexNoQuery,
  useUpdateAnnexMutation,
} from '@/store/api/annexesApi'
import { useListAllContractsQuery } from '@/store/api/contractsAllApi'
import { useGetLocalPQuery } from '@/store/api/localPApi'
import { useGetNPEContractQuery } from '@/store/api/npeContractsApi'
import type { AnnexCreateIn, AnnexOut, ContractAllItem } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
} from './dialogStyle'

export interface AnnexFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  fixedContractNo?: string
  initialValue?: AnnexOut | null
  onClose: () => void
  onSaved?: (id: number) => void
}

interface AnnexFormValues {
  contract_no: string
  annex_date: string
  content_before_change: string
  content_after_change: string
  price: number | ''
  quantity: number | ''
  note: string
}

const EMPTY: AnnexFormValues = {
  contract_no: '',
  annex_date: dayjs().format('YYYY-MM-DD'),
  content_before_change: '',
  content_after_change: '',
  price: '',
  quantity: '',
  note: '',
}

const schema: yup.ObjectSchema<AnnexFormValues> = yup.object({
  contract_no: yup.string().trim().required('Contract is required'),
  annex_date: yup.string().required('Amendment Date is required'),
  content_before_change: yup.string().trim().default(''),
  content_after_change: yup.string().trim().default(''),
  price: yup.mixed<number | ''>().default(''),
  quantity: yup.mixed<number | ''>().default(''),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<AnnexFormValues>

function fromAnnex(a: AnnexOut): AnnexFormValues {
  return {
    contract_no: a.contract_no ?? '',
    annex_date: a.annex_date ?? '',
    content_before_change: a.content_before_change ?? '',
    content_after_change: a.content_after_change ?? '',
    price: a.price ?? '',
    quantity: a.quantity ?? '',
    note: a.note ?? '',
  }
}

function toApi(v: AnnexFormValues): AnnexCreateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  return {
    contract_no: v.contract_no.trim(),
    annex_date: v.annex_date,
    content_before_change: str(v.content_before_change),
    content_after_change: str(v.content_after_change),
    price: num(v.price),
    quantity: num(v.quantity),
    note: str(v.note),
  }
}

const isNPESource = (item: ContractAllItem | null | undefined): boolean =>
  (item?.source ?? '') === 'npe' || (item?.contract_no ?? '').startsWith('NPE-')

const fmtNum = (v: number | null | undefined, digits = 2): string => {
  if (v == null) return ''
  return v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

export function AnnexFormDialog({
  open,
  mode,
  fixedContractNo,
  initialValue,
  onClose,
  onSaved,
}: AnnexFormDialogProps) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()
  const isEdit = mode === 'edit'

  const [createAnnex, { isLoading: creating }] = useCreateAnnexMutation()
  const [updateAnnex, { isLoading: updating }] = useUpdateAnnexMutation()
  const loading = creating || updating

  const { data: contractsRes } = useListAllContractsQuery({
    page: 1,
    page_size: 1_000_000,
  })
  const contractOptions = useMemo(() => contractsRes?.items ?? [], [contractsRes])
  const contractPOptions = useMemo(() => contractOptions.filter((c) => !isNPESource(c)), [contractOptions])
  const consignmentPOptions = useMemo(() => contractOptions.filter((c) => isNPESource(c)), [contractOptions])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AnnexFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  const selectedContractNo = watch('contract_no')
  const selectedIsNPE = selectedContractNo.startsWith('NPE-')

  // Detail fetch riêng từng nhánh:
  //  - LocalP: cần outright_price / quantity_kg fresh.
  //  - NPE: cần quantity_kg fresh — supplier_name dùng list cache cũng được vì
  //    không đổi giữa các phiên amendment.
  // Trước đây NPE chỉ đọc cached `contractOptions` → nếu user vừa sửa quantity
  // NPE rồi mở Amendment, sẽ amend trên qty cũ.
  const { data: localPDetailRes } = useGetLocalPQuery(selectedContractNo, {
    skip: !selectedContractNo || selectedIsNPE,
  })
  const localPDetail = localPDetailRes?.data
  const { data: npeDetailRes } = useGetNPEContractQuery(selectedContractNo, {
    skip: !selectedContractNo || !selectedIsNPE,
  })
  const npeDetail = npeDetailRes?.data
  const [lastFilled, setLastFilled] = useState('')

  const selectedContractItem = useMemo(
    () => contractOptions.find((c) => c.contract_no === selectedContractNo) ?? null,
    [contractOptions, selectedContractNo],
  )

  // Access parity: gen `AX-{contract_no}-{seq:02d}` khi chọn contract (create mode).
  const { data: suggestedAnnex } = useSuggestAnnexNoQuery(
    { contract_no: selectedContractNo },
    { skip: isEdit || !selectedContractNo },
  )
  const suggestedAnnexNo = suggestedAnnex?.suggestedNo ?? ''

  /* Annex KHÔNG bị race: BE `create_if_allowed_from_request` tự re-gen
   * annex_no từ `_next_annex_no(s, contract_no)` khi payload `annex_no` rỗng,
   * mà FE `toApi` không gửi field này → BE luôn gen mới ngay trong cùng
   * transaction save. 2 form New cùng lúc sẽ tự nhận 2 mã khác nhau (vì
   * MAX(pl_index) đã tăng sau form đầu commit).
   * → Skip apply skill access-suggest cho Annex. Endpoint check-no + hook
   *   `useLazyCheckAnnexNoExistsQuery` vẫn giữ cho future use case. */

  useEffect(() => {
    if (!open) return
    setLastFilled('')
    if (isEdit && initialValue) {
      reset(fromAnnex(initialValue))
    } else {
      reset({ ...EMPTY, contract_no: fixedContractNo ?? '' })
    }
  }, [open, isEdit, initialValue, fixedContractNo, reset])

  useEffect(() => {
    if (isEdit) return
    if (!selectedContractNo) return
    if (lastFilled === selectedContractNo) return
    if (selectedIsNPE) {
      // NPE: ưu tiên fresh detail (quantity_kg có thể vừa được sửa), fallback
      // list cache nếu detail chưa load xong.
      if (!npeDetail && !selectedContractItem) return
      setValue('price', '')
      setValue(
        'quantity',
        npeDetail?.quantity_kg ?? selectedContractItem?.quantity_kg ?? '',
      )
    } else {
      // LocalP: cần outright_price từ fresh detail; quantity ưu tiên list (MV),
      // fallback detail.
      if (!localPDetail) return
      setValue('price', localPDetail.outright_price ?? '')
      setValue(
        'quantity',
        selectedContractItem?.quantity_kg ?? localPDetail.quantity_kg ?? '',
      )
    }
    setLastFilled(selectedContractNo)
  }, [
    isEdit,
    localPDetail,
    npeDetail,
    selectedContractItem,
    selectedContractNo,
    selectedIsNPE,
    lastFilled,
    setValue,
  ])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApi(values)
      try {
        if (isEdit && initialValue) {
          const res = await updateAnnex({ id: initialValue.id, body: payload }).unwrap()
          snack.success('Amendment updated')
          onSaved?.(res.data?.id ?? initialValue.id)
        } else {
          const res = await createAnnex(payload).unwrap()
          snack.success('Amendment created')
          onSaved?.(res.data?.id ?? 0)
        }
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(err, isEdit ? 'Failed to update amendment' : 'Failed to create amendment'),
        )
      }
    },
    () => snack.error('Please fix the highlighted fields'),
  )

  const supplierDisplay = useMemo(() => {
    // Ưu tiên contractOptions (list view đã JOIN cp_vn) → instant từ cache.
    const fromList =
      selectedContractItem?.supplier_name
      || selectedContractItem?.short_name
    if (fromList) return fromList
    // Fallback: LocalP detail (sau khi fetch xong, backend JOIN cp_vn).
    if (!selectedIsNPE && localPDetail) {
      return localPDetail.supplier_name || localPDetail.short_name || ''
    }
    return ''
  }, [selectedContractItem, selectedIsNPE, localPDetail])

  // Quantity: contractOptions có sẵn (list trả quantity_kg) → instant.
  const contextQuantity =
    selectedContractItem?.quantity_kg ?? (selectedIsNPE ? null : localPDetail?.quantity_kg ?? null)
  // Price fields chỉ áp dụng cho LocalP — NPE luôn empty.
  const contextPriceUnit = selectedIsNPE ? '' : localPDetail?.price_unit ?? ''
  const contextOutright = selectedIsNPE ? null : localPDetail?.outright_price ?? null
  const contextPtbfDiffs = selectedIsNPE ? null : localPDetail?.ptbf_diffs ?? null

  const contractPSelected = useMemo<ContractAllItem | null>(() => {
    if (!selectedContractNo || selectedIsNPE) return null
    return (
      contractPOptions.find((c) => c.contract_no === selectedContractNo) ?? {
        contract_no: selectedContractNo,
        short_name: null,
        cp_code: null,
        contract_date: null,
        license_cert_no: null,
        certification: null,
        source: 'local_p',
      }
    )
  }, [selectedContractNo, selectedIsNPE, contractPOptions])

  const consignmentPSelected = useMemo<ContractAllItem | null>(() => {
    if (!selectedContractNo || !selectedIsNPE) return null
    return (
      consignmentPOptions.find((c) => c.contract_no === selectedContractNo) ?? {
        contract_no: selectedContractNo,
        short_name: null,
        cp_code: null,
        contract_date: null,
        license_cert_no: null,
        certification: null,
        source: 'npe',
      }
    )
  }, [selectedContractNo, selectedIsNPE, consignmentPOptions])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title="Purchase - Amendment" sx={dialogTitleTextSx}>
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Purchase - </Box>Amendment
          {isEdit && initialValue?.id ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              #{initialValue.id}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void onSubmit()}
            disabled={loading || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {/* Row 1: Contract P (6) | Consignment P (6) */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="contract_no"
              render={({ field }) => (
                <Autocomplete
                  size="small"
                  options={contractPOptions}
                  value={contractPSelected}
                  onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                  getOptionLabel={(opt) =>
                    opt ? `${opt.contract_no} — ${opt.short_name ?? opt.cp_code ?? ''}` : ''
                  }
                  isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                  disabled={isEdit || !!fixedContractNo}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contract P"
                      error={!!errors.contract_no && !selectedIsNPE}
                      helperText={!selectedIsNPE ? errors.contract_no?.message : undefined}
                      sx={isEdit || !!fixedContractNo ? roSx : undefined}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="contract_no"
              render={({ field }) => (
                <Autocomplete
                  size="small"
                  options={consignmentPOptions}
                  value={consignmentPSelected}
                  onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                  getOptionLabel={(opt) =>
                    opt ? `${opt.contract_no} — ${opt.short_name ?? opt.cp_code ?? ''}` : ''
                  }
                  isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                  disabled={isEdit || !!fixedContractNo}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Consignment P"
                      error={!!errors.contract_no && selectedIsNPE}
                      helperText={selectedIsNPE ? errors.contract_no?.message : undefined}
                      sx={isEdit || !!fixedContractNo ? roSx : undefined}
                    />
                  )}
                />
              )}
            />
          </Grid>

          {/* Row 2: Warning label */}
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: 'warning.main', fontStyle: 'italic' }}>
              * Only choose one contract: Contract P or Consignment P
            </Typography>
          </Grid>

          {/* Row 3: Supplier (full) */}
          <Grid item xs={12}>
            <TextField
              label="Supplier"
              size="small"
              fullWidth
              value={supplierDisplay}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>

          {/* Row 4: Quantity (contract) | Price Unit */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Quantity (contract)"
              size="small"
              fullWidth
              value={fmtNum(contextQuantity, 2)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={[numSx, roSx]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Price Unit"
              size="small"
              fullWidth
              value={contextPriceUnit}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>

          {/* Row 5: Outright Price | PTBF Diffs */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Outright Price"
              size="small"
              fullWidth
              value={fmtNum(contextOutright, 2)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={[numSx, roSx]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="PTBF Diffs"
              size="small"
              fullWidth
              value={contextPtbfDiffs == null ? '' : `$ ${fmtNum(contextPtbfDiffs, 2)}`}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={[numSx, roSx]}
            />
          </Grid>

          {/* Row 6: Amendment No (RO) | Amendment Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Amendment No"
              size="small"
              fullWidth
              value={isEdit ? initialValue?.annex_no ?? '' : suggestedAnnexNo}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              helperText={
                isEdit
                  ? undefined
                  : selectedContractNo
                    ? 'Suggested — will be assigned on save'
                    : 'Choose a contract to see suggestion'
              }
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="annex_date"
              render={({ field }) => (
                <DatePicker
                  label="Amendment Date *"
                  format="DD-MMM-YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: !!errors.annex_date,
                      helperText: errors.annex_date?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Row 7: Price | Quantity (Kg) */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="price"
              render={({ field }) => (
                <TextField
                  label="Price"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!errors.price}
                  helperText={errors.price?.message}
                  sx={numSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="quantity"
              render={({ field }) => (
                <TextField
                  label="Quantity (Kg)"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'numeric' }}
                  error={!!errors.quantity}
                  helperText={errors.quantity?.message}
                  sx={numSx}
                />
              )}
            />
          </Grid>

          {/* Row 8: Content before changed | Content after changed */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="content_before_change"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Content before changed"
                  size="small"
                  multiline
                  minRows={3}
                  fullWidth
                  error={!!errors.content_before_change}
                  helperText={errors.content_before_change?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="content_after_change"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Content after changed"
                  size="small"
                  multiline
                  minRows={3}
                  fullWidth
                  error={!!errors.content_after_change}
                  helperText={errors.content_after_change?.message}
                />
              )}
            />
          </Grid>

          {/* Row 9: Note (full) */}
          <Grid item xs={12}>
            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Note"
                  size="small"
                  multiline
                  minRows={2}
                  fullWidth
                  error={!!errors.note}
                  helperText={errors.note?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default AnnexFormDialog
